"use client";

/**
 * Client-component variant of <AdSlot>. Fetches the resolved banner
 * from `/api/ads/slot/[placement]` and renders it with the same
 * creative-type switch (IMAGE / GIF / VIDEO / HTML) as the RSC.
 *
 * When the `ads.rotation` flag is on, the endpoint also returns the full
 * list of eligible creatives + rotation config, and this component rotates
 * through them every 15s via `useAdRotation` (viewable-only impressions,
 * pauses off-screen / in background, CLS-safe cross-fade). With the flag off
 * (or a single creative) the behaviour is identical to before.
 *
 * Used inside other client components (product detail, etc.) where
 * the server <AdSlot> can't be imported. Falls back to a labeled
 * dashed placeholder in dev so editors see exactly where each slot
 * lives, even before an ad is configured in admin.
 */
import { useEffect, useState } from "react";
import { getPlacementSpec } from "@/lib/ad-placements";
import { getAdSlotStyle, getPlacementAdSize } from "@/lib/ad-sizing";
import { cn } from "@/lib/utils";
import { useUserInteraction } from "@/hooks/use-user-interaction";
import { useAdRotation, type RotationConfig } from "./use-ad-rotation";

interface Banner {
  id: string;
  campaignId?: string | null;
  creativeId?: string | null;
  title: string;
  imageUrl: string | null;
  alt: string | null;
  html?: string | null;
  videoUrl?: string | null;
  creativeType?: string | null;
  widthPx?: number | null;
  heightPx?: number | null;
  goHref?: string | null;
  placement?: string | null;
}

interface SlotResponse {
  data: Banner | null;
  rotation?: {
    enabled: boolean;
    intervalMs: number;
    maxRotations: number;
    items: Banner[];
  } | null;
}

interface AdSlotClientProps {
  placement: string;
  country?: string;
  className?: string;
  category?: string | null;
  brand?: string | null;
  productId?: string | null;
  /** Section/carousel id (eg. "DailyOffers_V1") for `targetSections`. */
  section?: string | null;
  /** Route the slot is rendering on (eg. "/comparador") for `onlyRoutes`. */
  route?: string | null;
  /** Forces the labeled placeholder regardless of the user's role. */
  showPlaceholder?: boolean;
}

const HTML_CREATIVE_CLASS =
  "block h-full w-full [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:max-w-full [&_iframe]:border-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_video]:h-full [&_video]:w-full [&_video]:object-cover";

export function AdSlotClient({
  placement,
  country = "MX",
  className,
  category,
  brand,
  productId,
  section,
  route,
  showPlaceholder,
}: AdSlotClientProps) {
  const interacted = useUserInteraction();
  const [items, setItems] = useState<Banner[]>([]);
  const [rotation, setRotation] = useState<RotationConfig | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Whether the current user is allowed to see empty-state placeholders
  // (admin / mods / selected emails / DIAMOND tier). Defaults to false
  // until we hear back from the server.
  const [previewAllowed, setPreviewAllowed] = useState(false);

  // Rotation hook MUST run unconditionally (rules of hooks); it tolerates an
  // empty list and a null config (no-op until data arrives).
  const { containerRef, current } = useAdRotation(items, rotation);
  const slotStyle = getAdSlotStyle(placement);
  const hasSlotSize = Boolean(getPlacementAdSize(placement));

  useEffect(() => {
    // Wait for scroll / pointer / keyboard so the fetch + paint land after
    // first interaction and the reserved slot box does not shift on load.
    if (!interacted) return;
    let active = true;
    const qs = new URLSearchParams();
    qs.set("country", country);
    qs.set("rotate", "1"); // server gates real rotation behind the `ads.rotation` flag
    if (category) qs.set("category", category);
    if (brand) qs.set("brand", brand);
    if (productId) qs.set("productId", productId);
    if (section) qs.set("section", section);
    if (route) qs.set("route", route);
    Promise.all([
      fetch(`/api/ads/slot/${encodeURIComponent(placement)}?${qs.toString()}`, {
        cache: "no-store",
      }).then((r) => (r.ok ? (r.json() as Promise<SlotResponse>) : null)),
      fetch(`/api/ads/preview-allowed`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => Boolean(j?.data?.allowed))
        .catch(() => false),
    ])
      .then(([slotJson, allowed]) => {
        if (!active) return;
        const rot = slotJson?.rotation ?? null;
        if (rot && rot.items.length > 0) {
          setItems(rot.items);
          setRotation({
            enabled: rot.enabled,
            intervalMs: rot.intervalMs,
            maxRotations: rot.maxRotations,
          });
        } else {
          setItems(slotJson?.data ? [slotJson.data] : []);
          setRotation(null);
        }
        setPreviewAllowed(allowed);
        setLoaded(true);
      })
      .catch(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [interacted, placement, country, category, brand, productId, section, route]);

  // Reserve the placement box before the creative arrives so revealing the
  // ad after interaction does not push surrounding content (CLS ~0).
  if (!interacted || !loaded) {
    if (showPlaceholder) {
      return (
        <div
          className={cn("animate-pulse bg-muted/30 rounded-xl", className)}
          style={slotStyle}
          aria-hidden
        />
      );
    }
    if (hasSlotSize) {
      return (
        <div
          data-ad-placement={placement}
          data-ad-pending={interacted ? "load" : "interaction"}
          style={slotStyle}
          className={cn("relative overflow-hidden rounded-xl", className)}
          aria-hidden
        />
      );
    }
    return null;
  }

  if (!current) {
    // Only admins / mods / selected users see the labeled placeholder;
    // regular visitors get nothing so empty slots collapse cleanly.
    const allow = showPlaceholder ?? previewAllowed;
    if (!allow) return null;
    const spec = getPlacementSpec(placement);
    return (
      <div
        data-ad-placement={placement}
        data-ad-empty="true"
        style={slotStyle}
        className={cn(
          "relative rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/20 p-3 text-center text-[11px] text-muted-foreground",
          className,
        )}
      >
        <div className="font-semibold tracking-wide uppercase">{spec?.label ?? placement}</div>
        <div className="mt-0.5">{spec?.size ?? "Espacio publicitario"}</div>
      </div>
    );
  }

  const banner = current;
  const type = (banner.creativeType ?? "IMAGE").toUpperCase();
  const href = banner.goHref ?? `/api/ads/go/${banner.id}`;
  const blockClass = hasSlotSize ? "block h-full w-full" : "block w-full";
  const mediaClass = hasSlotSize ? "h-full w-full object-cover" : "h-auto w-full object-cover";

  return (
    <div
      ref={containerRef}
      data-ad-placement={placement}
      data-ad-banner-id={banner.id}
      data-ad-campaign-id={banner.campaignId ?? undefined}
      data-ad-creative-id={banner.creativeId ?? undefined}
      style={slotStyle}
      className={cn("relative overflow-hidden rounded-xl border bg-card", className)}
    >
      {/* `key` on the creative makes React remount on rotation → fade-in swap
          without touching the container box, so layout never shifts (CLS ~0). */}
      <div
        key={banner.id}
        className="h-full w-full motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500"
      >
        {type === "HTML" && banner.html ? (
          <a
            href={href}
            target="_blank"
            rel="noopener sponsored"
            aria-label={banner.alt ?? banner.title}
            className={cn("isolate", hasSlotSize ? HTML_CREATIVE_CLASS : "block w-full")}
            dangerouslySetInnerHTML={{ __html: banner.html }}
          />
        ) : type === "VIDEO" && banner.videoUrl ? (
          <a
            href={href}
            target="_blank"
            rel="noopener sponsored"
            aria-label={banner.alt ?? banner.title}
            className={blockClass}
          >
            <video
              src={banner.videoUrl}
              poster={banner.imageUrl || undefined}
              autoPlay
              muted
              loop
              playsInline
              className={mediaClass}
            />
          </a>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener sponsored"
            aria-label={banner.alt ?? banner.title}
            className={blockClass}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {banner.imageUrl && (
              <img
                src={banner.imageUrl}
                alt={banner.alt ?? banner.title}
                className={mediaClass}
                loading="lazy"
              />
            )}
          </a>
        )}
      </div>
      <span className="absolute top-1 right-2 text-[9px] uppercase tracking-wider text-muted-foreground bg-background/80 backdrop-blur-sm px-1.5 py-0.5 rounded">
        Patrocinado
      </span>
    </div>
  );
}
